/**
 * 툴박스에서 끌 때의 드래그 이미지 — **캔버스에 놓였을 때의 노드 모양**으로 보여 준다.
 *
 * 브라우저 기본 드래그 이미지는 끄는 요소(툴박스 항목)를 그대로 찍어서, 놓기 전까지 툴박스 모양으로 보였다.
 * 노드 컴포넌트(nodes/BaseNode)는 React Flow 안에서만 그릴 수 있어, 같은 CSS 클래스·도형·아이콘으로 겉모습만 복제한다
 * (노드 상자 105×56 + 안의 아이콘·유형명 + 아래 이름). 크기는 지금 캔버스의 확대 비율을 따른다.
 * 마우스는 노드 **가운데**를 잡는다 — 캔버스의 드롭도 그에 맞춰 가운데를 기준으로 놓는다(GHOST_CENTERED).
 */
import styles from '../nodes/WorkflowNode.module.css';
import { shapeClassOf, typeLabelOf } from '../nodeCatalog';

export const NODE_W = 105;
export const NODE_H = 56;

/** 드롭 데이터에 이 표시가 있으면 캔버스는 커서를 노드 가운데로 보고 놓는다 */
export const GHOST_CENTERED = 'ghostCentered';

/** 지금 캔버스 확대 비율 — 캔버스가 없으면 1 */
export function canvasZoom(): number {
  const vp = document.querySelector('.react-flow__viewport') as HTMLElement | null;
  if (!vp) return 1;
  const m = new DOMMatrixReadOnly(getComputedStyle(vp).transform);
  return m.a || 1;
}

/**
 * 노드 모양 드래그 이미지를 걸어 준다. iconSource 는 아이콘 <svg> 를 가진 요소(툴박스 항목) — 그 그림을 복제한다.
 * 실패해도 기본 드래그 이미지로 계속된다(끌기 자체는 막지 않는다).
 */
export function setNodeDragImage(e: React.DragEvent, type: string, label: string, iconSource?: Element | null): void {
  try {
    const zoom = canvasZoom();
    const host = document.createElement('div');
    // 화면 밖에 잠깐 그린다 — setDragImage 는 그 순간의 모습을 찍는다. 노드 아래 이름까지 들어가게 높이를 넉넉히
    host.style.cssText = `position:fixed;left:-2000px;top:-2000px;width:${NODE_W + 80}px;height:${NODE_H + 46}px;`
      + `display:flex;justify-content:center;pointer-events:none;zoom:${zoom};`;

    const box = document.createElement('div');
    box.className = `relative shadow-md ${shapeClassOf(type)} bg-white border-2 border-gray-200 ${styles.node} px-4 py-2`;
    box.style.cssText = `width:${NODE_W}px;height:${NODE_H}px;min-width:${NODE_W}px;min-height:${NODE_H}px;flex:none;`;

    const row = document.createElement('div');
    row.className = 'flex items-center justify-center gap-1.5 w-full px-1';
    const iconWrap = document.createElement('span');
    iconWrap.className = 'shrink-0 flex items-center justify-center text-gray-600';
    const svg = iconSource?.querySelector('svg');
    if (svg) {
      const icon = svg.cloneNode(true) as SVGElement;
      icon.setAttribute('width', '24');
      icon.setAttribute('height', '24');
      icon.style.width = '24px';
      icon.style.height = '24px';
      iconWrap.appendChild(icon);
    }
    const typeText = document.createElement('span');
    typeText.className = 'text-xs font-normal text-gray-500 truncate max-w-[60px] leading-tight';
    typeText.textContent = typeLabelOf(type);
    row.appendChild(iconWrap);
    row.appendChild(typeText);
    box.appendChild(row);

    if (label) {
      const name = document.createElement('div');
      name.textContent = label;
      name.style.cssText = 'position:absolute;top:100%;left:50%;transform:translateX(-50%);margin-top:7px;max-width:170px;'
        + 'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:0.75rem;line-height:1rem;font-weight:500;'
        + 'color:rgb(var(--c-gray-800));text-align:center;';
      box.appendChild(name);
    }
    host.appendChild(box);
    document.body.appendChild(host);
    // zoom 이 걸린 요소의 좌표는 확대된 크기 기준이다 — 가운데를 잡는다
    const hostW = (NODE_W + 80) * zoom;
    e.dataTransfer.setDragImage(host, hostW / 2, (NODE_H / 2) * zoom);
    window.setTimeout(() => host.remove(), 0);
  } catch {
    /* 기본 드래그 이미지로 */
  }
}
